import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { CategoryAccordion } from "component/shared/category-accordion";
import { DialogFrame } from "component/shared/dialog-frame";
import { PageHeader } from "component/shared/page-header";
import { RefreshTable } from "component/shared/refresh-table";
import { Column, Sort } from "component/shared/table";
import { PageProps } from "shared/page-props";
import { WidgetType } from "type/widget-type";
import { QuickSearchConfig } from "./widgets/quick-search";
import { AgentListingsConfig } from "./widgets/agent-listings";
import { ContactFormConfig } from "./widgets/contact-form";
import { FeaturedListingsConfig } from "./widgets/featured-listings";
import { OfficeListingsConfig } from "./widgets/office-listings";
import { ListingsConfig } from "./widgets/listings";
import { MarketReportSignupConfig } from "./widgets/market-report-signup";
import { ValuationFormConfig } from "./widgets/valuation-form";
import { RegistrationFormConfig } from "./widgets/registration-form";
import { GallerySliderConfig } from "./widgets/gallery-slider";
import { ListingSearchConfig } from "./widgets/listing-search";
import { LoginConfig } from "./widgets/login";
import { MarketsConfig } from "./widgets/markets";
import { MarketReportConfig } from "./widgets/market-report";
import { WidgetEntry, WidgetGroup, widgetGroups } from "./widget-catalog";
import { styles } from "./style";

const COPY = {
	title: "Widgets",
	description: "Generate embed code for IDX widgets to add to any page on your site. Select a widget type, configure its options, then copy and paste the code.",
};

// Rows keep the catalogue's order; nothing in the section sorts.
const NO_SORT: Sort = { id: "", desc: false };

const DIALOG_TITLE_ID = "widget-embed-code-title";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {
}

/** The form for one widget, shown in its Generate Embed Code modal. */
const widgetConfiguration = (widgetType: WidgetType) => {
	switch(widgetType) {
		case WidgetType.LISTINGS:
			return <ListingsConfig />;
		case WidgetType.FEATURED_LISTINGS:
			return <FeaturedListingsConfig />;
		case WidgetType.AGENT_LISTINGS:
			return <AgentListingsConfig />;
		case WidgetType.OFFICE_LISTINGS:
			return <OfficeListingsConfig />;
		case WidgetType.MARKETS:
			return <MarketsConfig />;
		case WidgetType.MARKET_REPORT:
			return <MarketReportConfig />;
		case WidgetType.MARKET_REPORT_SIGNUP:
			return <MarketReportSignupConfig />;
		case WidgetType.VALUATION_FORM:
			return <ValuationFormConfig />;
		case WidgetType.LOGIN:
			return <LoginConfig />;
		case WidgetType.REGISTRATION_FORM:
			return <RegistrationFormConfig />;
		case WidgetType.GALLERY_SLIDER:
			return <GallerySliderConfig />;
		case WidgetType.LISTING_SEARCH:
			return <ListingSearchConfig />;
		case WidgetType.QUICK_SEARCH:
			return <QuickSearchConfig />;
		case WidgetType.CONTACT_FORM:
			return <ContactFormConfig />;
		default:
			return false;
	}
};

const isPermitted = (user: PageProps["user"]) => {
	const isKestrelAll = user.kestrelVersion.all;
	let permitted = false;
	if (user) {
		if (isKestrelAll && !user.websitePlatform.wordpress) {
			permitted = true;
		}
		if (!isKestrelAll) {
			if (user.permissions.sidebarWidgets) {
				permitted = true;
			}
		}
	}
	return permitted;
};

/**
 * The Widgets page (MAX-4403; Figma page 27). The widgets sit in collapsible categories, collapsed
 * by default, and each opens its own Generate Embed Code modal. It stores nothing: the embed code is
 * built on the client. v9 clients still get the ColdFusion widget selector in an iframe.
 */
const Component: React.FC<Props> = ({ classes = {}, user }) => {
	const [expanded, setExpanded] = useState<string[]>([]);
	const [open, setOpen] = useState(false);
	// Kept after close so the modal's title doesn't change while it fades out.
	const [widget, setWidget] = useState<WidgetEntry>();
	const toggle = (label: string) =>
		setExpanded((current) => (current.includes(label) ? current.filter((l) => l !== label) : [...current, label]));
	const generate = (entry: WidgetEntry) => {
		setWidget(entry);
		setOpen(true);
	};

	const columns: Column<WidgetEntry>[] = [
		{
			id: "widget",
			title: "Widget",
			value: (entry) => entry.type.label,
		},
		{
			id: "description",
			title: "Description",
			value: (entry) => entry.description,
		},
		{
			id: "actions",
			title: "Action",
			align: "right",
			value: (entry) => (
				<Mui.Button
					variant="text"
					color="primary"
					className={classes.action}
					endIcon={<Icons.ArrowRight size={16} />}
					onClick={() => generate(entry)}
					aria-label={`Generate ${entry.type.label} embed code`}
				>
					Generate Embed Code
				</Mui.Button>
			),
		},
	];

	const kestrelAll = user.kestrelVersion.all;
	const groups: WidgetGroup[] = kestrelAll ? widgetGroups(user.permissions) : [];
	return (
		<DashboardLayout
			permitted={isPermitted(user)}
			title={COPY.title}
			// The v9 selector in the iframe has its own copy, so the v10 description is for v10 only.
			header={<PageHeader title={COPY.title} description={kestrelAll ? COPY.description : undefined} />}
			iframe={!kestrelAll &&
				<ColdFusionIframe url="/WidgetSelector.cfm" />
			}
			fullScreen={false}
		>
			{kestrelAll && (
				<div className={classes.list}>
					{groups.map((group) => {
						const isOpen = expanded.includes(group.label);
						return (
							<CategoryAccordion
								key={group.label}
								className={classes.accordion}
								summaryClassName={classes.summary}
								expanded={isOpen}
								onToggle={() => toggle(group.label)}
								disableDetailsPadding
								summary={
									<div className={classes.header}>
										<span className={classes.title}>
											{isOpen ? (
												<Icons.ChevronUp size={24} className={classes.chevron} />
											) : (
												<Icons.ChevronDown size={24} className={classes.chevron} />
											)}
											{group.label}
										</span>
									</div>
								}
							>
								<div className={classes.details}>
									<RefreshTable
										items={group.widgets}
										columns={columns}
										sort={NO_SORT}
										rowKey={(entry) => entry.type.id}
										paginate={false}
										embedded
									/>
								</div>
							</CategoryAccordion>
						);
					})}
				</div>
			)}
			{widget && (
				<DialogFrame
					open={open}
					onClose={() => setOpen(false)}
					title={`Generate ${widget.type.label} Embed Code`}
					titleId={DIALOG_TITLE_ID}
					width={840}
				>
					{widgetConfiguration(widget.type)}
				</DialogFrame>
			)}
		</DashboardLayout>
	);
};

export const SettingsWidgetsPage = withStyles(Component, styles);
